import 'components/ordering/speed-order/components/speed-order-form-row/speed-order-form-row.js'
import 'components/ordering/speed-order/components/speed-order-slide-in/speed-order-slide-in.js'
import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { repeat } from 'lit/directives/repeat.js'
import { customElement, query, queryAll, state, property } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { style } from './speed-order.style.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import { BREAKPOINTS, ConcreteBreakpoints } from '@cloudsuite/mosaic-shared/scripts/breakpoints.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { 
  ProductSearchResult, 
  SpeedOrderSearchAPI,
  SpeedOrderSearchRequest,
  SPEED_ORDER_SEARCH_API
} from 'components/ordering/speed-order/services/speed-order-search-api.js'
import {
  SpeedOrderUploadAPI,
  SpeedOrderUploadRequest,
  SpeedOrderValidateRequest,
  SpeedOrderImportRequest,
  UploadResult,
  SPEED_ORDER_UPLOAD_API
} from 'components/ordering/speed-order/services/speed-order-upload-api.js'
import {
  SpeedOrderAddToBasketAPI,
  SpeedOrderAddToBasketRequest,
  SPEED_ORDER_ADD_TO_BASKET_API
} from 'components/ordering/speed-order/services/speed-order-add-to-basket-api.js'
import { type CSEUploadValidatedEvent, type CSEUploadValidationErrorEvent } from '@cloudsuite/mosaic-elements'
import { showToast } from 'components/shared/toast-host/toast-host.js'
import { ProductAddedEvent } from 'components/ordering/add-to-basket/events.js'
import type { CSCSpeedOrderFormRow } from 'components/ordering/speed-order/components/speed-order-form-row/speed-order-form-row.js'



export interface SpeedOrderRow {
  rowId?: string
  filled?: boolean | undefined
  productName?: string | undefined
  productCode?: string | undefined
  price?: number | undefined
  quantity?: string | number | undefined
  productId?: number | undefined
  isSearching?: boolean | undefined
  hasError?: boolean | undefined
  defaultUom?: string | undefined
  pricePerPriceUom?: string | undefined
  priceUom?: string | undefined
  orderMinimumQty?: number | undefined
  orderDefaultQty?: number | undefined
  currencySymbol?: string | undefined
}

export type UomSource = 'default' | 'price'

@customElement('csc-speed-order')
export class CSCSpeedOrder extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @query('csc-speed-order-slide-in')
  private uploadSlideIn!: any

  @queryAll('csc-speed-order-form-row')
  private formRows!: NodeListOf<CSCSpeedOrderFormRow>

  private nextRowId = 0

  @state()
  private rows: Array<SpeedOrderRow> = []

  @state()
  private isMobileScreen = false

  @state()
  private isUploading = false

  @state()
  private uploadResult: UploadResult | null = null

  @state()
  private importResult: any = null

  @state()
  private csvConfig: any = null

  @state()
  private slideInVariant: 'success' | 'error' | 'warning' = 'success'

  @state()
  private isOrderAllLoading = false

  @state()
  private isDragOver = false

  @state()
  private fileValidationError = ''

  @property({ type: String, attribute: 'search-product-url' })
  searchProductUrl = ''

  @property({ type: String, attribute: 'upload-url' })
  uploadUrl = ''

  @property({ type: String, attribute: 'validate-url' })
  validateUrl = ''

  @property({ type: String, attribute: 'import-url' })
  importUrl = ''

  @property({ type: String, attribute: 'add-to-basket-url' })
  addToBasketUrl = ''

  @property({ type: String, attribute: 'basket-url' })
  basketUrl = ''

  @property({ type: String, attribute: 'home-url' })
  homeUrl = ''

  @property({ type: String, attribute: 'data-example-url' })
  dataExampleUrl = ''

  @property({ type: Boolean, attribute: 'disable-upload' })
  disableUpload = false

  @property({ type: String, attribute: 'uom-source' })
  uomSource: UomSource = 'default'

  @inject(BREAKPOINTS)
  private breakpoints!: ConcreteBreakpoints

  @inject(SPEED_ORDER_SEARCH_API)
  private searchService!: SpeedOrderSearchAPI

  @inject(SPEED_ORDER_UPLOAD_API)
  private uploadService!: SpeedOrderUploadAPI

  @inject(SPEED_ORDER_ADD_TO_BASKET_API)
  private addToBasketService!: SpeedOrderAddToBasketAPI

  private hasHeaders = false

  constructor() {
    super()
    this.rows = Array.from({ length: 10 }, () => this.createEmptyRow())
    this.breakpoints.matchMedia(breakpoints.medium)
    createEffect(() => {
      this.isMobileScreen = this.breakpoints.small()
      if (this.isMobileScreen) {
        this.setAttribute('mobile-screen', '')
      } else {
        this.removeAttribute('mobile-screen')
      }
    })

    if (!this.disableUpload) {
      // Add global drag end listener to handle drag cancellation
      this.addEventListener('dragend', this.handleDragEnd)

      // Add file validation error listener
      this.addEventListener('cse-upload-validation-error', this.handleFileValidationError as EventListener)
    }
  }

  // File upload functionality
  private handleFileUpload = async (event: CSEUploadValidatedEvent | CustomEvent) => {
    // Handle both CSEUploadValidatedEvent and custom event from file input
    let file: File | null = null
    if ('file' in event) {
      // CSEUploadValidatedEvent
      file = event.file
    } else if (event.detail?.file) {
      // Custom event from file input
      file = event.detail.file
    }
    
    if (!file) return

    this.isUploading = true
    
    try {
      // Step 1: Upload the file to get CSV config and file ID
      const uploadRequest = new SpeedOrderUploadRequest(this.uploadUrl, file)
      const uploadResult = await this.uploadService.upload(uploadRequest)
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.message || 'Upload failed')
      }

      // Store the upload result and CSV config for later use
      this.uploadResult = uploadResult
      this.csvConfig = {
        csv_id: uploadResult.csv_id,
        csv_settings: uploadResult.csv_settings,
        col_headers: uploadResult.header_cols && uploadResult.header_cols.length > 0,
        col_code: 0,
        col_qty: 1
      }
      this.hasHeaders = !!(uploadResult.header_cols && uploadResult.header_cols.length > 0)
      
      // Pass header information to slide-in component
      this.uploadSlideIn.columnHeaders = uploadResult.header_cols || []
      this.uploadSlideIn.hasHeaders = this.hasHeaders
      
      // Show success state and let user trigger validation
      this.slideInVariant = 'success'
      this.uploadSlideIn.open()
    } catch (error) {
      console.error('Upload failed:', error)
      this.uploadResult = {
        success: false,
        message: 'Upload failed. Please try again.',
        errors: [error instanceof Error ? error.message : 'Unknown error']
      }
      this.slideInVariant = 'error'
      this.uploadSlideIn.open()
    } finally {
      this.isUploading = false
    }
  }

  // Method to validate CSV (called from slide-in component)
  public validateAndImportCSV = async (event?: CustomEvent) => {
    if (!this.csvConfig?.csv_id) {
      console.error('No CSV config available for validation')
      return
    }

    // Update CSV config with user selections from slide-in
    if (event?.detail) {
      this.csvConfig = {
        ...this.csvConfig,
        col_headers: event.detail.hasHeaders,
        col_code: event.detail.codeColumn,
        col_qty: event.detail.quantityColumn
      }
    }

    try {
      // Step 2: Validate the CSV
      const validateRequest = new SpeedOrderValidateRequest(
        this.validateUrl,
        this.csvConfig.csv_id,
        this.csvConfig.csv_settings,
        this.csvConfig.col_headers,
        this.csvConfig.col_code,
        this.csvConfig.col_qty
      )
      const validateResult = await this.uploadService.validate(validateRequest)

      // Step 3: Import the CSV data (following React pattern)
      const importRequest = new SpeedOrderImportRequest(
        this.importUrl,
        this.csvConfig.csv_id,
        this.csvConfig.csv_settings,
        validateResult.errors,
        validateResult.corrections,
        this.csvConfig.col_headers,
        this.csvConfig.col_code,
        this.csvConfig.col_qty
      )
      const importResult = await this.uploadService.import(importRequest)

      // Store the import result for later use
      this.importResult = importResult

      if (validateResult.corrections && validateResult.corrections.length > 0) {
        this.slideInVariant = 'warning'
        this.uploadSlideIn.corrections = validateResult.corrections
        if (validateResult.errors && validateResult.errors.length > 0) {
          this.uploadSlideIn.errors = [...validateResult.errors]
        }
        this.uploadSlideIn.records = importResult.records ? importResult.records.length : 0
        this.uploadSlideIn.open()
      } else if (validateResult.errors && validateResult.errors.length > 0) {
        this.slideInVariant = 'error'
        this.uploadSlideIn.errors = [...validateResult.errors]
        this.uploadSlideIn.records = importResult.records ? importResult.records.length : 0
        this.uploadSlideIn.open()
      } else if (importResult.records && importResult.records.length > 0) {
        this.processImportedRecords(importResult.records)
        this.uploadSlideIn.close()
      } else {
        this.slideInVariant = 'error'
        const errorMessage = importResult.message || 'No records found to import'
        console.error('Import failed:', errorMessage)
        console.error('Full import result:', importResult)
      }
      
    } catch (error) {
      console.error('Validation/Import failed:', error)
      this.slideInVariant = 'error'
    }
  }

  private handleFileValidationError = (event: CSEUploadValidationErrorEvent) => {
    this.slideInVariant = 'error'
    this.uploadSlideIn.errors = [{ row: 0, product_number: '', name: '', quantity: 0, message: event.error || 'File validation failed' }]
    this.uploadSlideIn.open()
  }

  // New file input handling methods
  private triggerFileUpload = () => {
    const fileInput = this.shadowRoot?.getElementById('csv-file-input') as HTMLInputElement
    if (fileInput) {
      fileInput.click()
    }
  }

  private handleFileInputChange = (event: Event) => {
    const target = event.target as HTMLInputElement
    if (target.files && target.files.length > 0) {
      const file = target.files[0]
      if (file) {
        // Check if it's a CSV file
        if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
          // Clear any previous error
          this.fileValidationError = ''
          // Create a custom event to simulate the CSEUploadValidatedEvent
          const customEvent = new CustomEvent('file-validated', {
            detail: { file },
            bubbles: true,
            composed: true
          })
          // Call the existing handler with the custom event
          this.handleFileUpload(customEvent as any)
        } else {
          // Show error for non-CSV files
          this.fileValidationError = 'Please upload a file of type: text/csv'
        }
      }
      // Reset the input value to allow selecting the same file again
      target.value = ''
    }
  }

  // Drag and drop functionality
  private dragCounter = 0

  private handleDragOver = (event: DragEvent) => {
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  private handleDragEnter = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    
    // Only count drag enter on the main container, not child elements
    if (event.currentTarget === this.shadowRoot?.querySelector('.speed-order-container')) {
      this.dragCounter++
      if (this.dragCounter === 1) {
        this.isDragOver = true
      }
    }
  }

  private handleDragLeave = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    
    // Only count drag leave on the main container, not child elements
    if (event.currentTarget === this.shadowRoot?.querySelector('.speed-order-container')) {
      this.dragCounter--
      if (this.dragCounter === 0) {
        this.isDragOver = false
      }
    }
  }

  private handleDrop = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    
    // Reset drag state
    this.dragCounter = 0
    this.isDragOver = false
    
    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
      const file = files[0]
      if (file && file.type) { // Add null check for file and type
        // Check if it's a CSV file
        if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
          // Clear any previous error
          this.fileValidationError = ''
          // Create a custom event to simulate the CSEUploadValidatedEvent
          const customEvent = new CustomEvent('file-validated', {
            detail: { file },
            bubbles: true,
            composed: true
          })
          // Call the existing handler with the custom event
          this.handleFileUpload(customEvent as any)
        } else {
          // Show error for non-CSV files
          this.fileValidationError = 'Please upload a file of type: text/csv'
        }
      }
    }
  }

  // Handle drag end to reset state if drag is cancelled
  private handleDragEnd = () => {
    this.dragCounter = 0
    this.isDragOver = false
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    if (!this.disableUpload) {
      this.removeEventListener('dragend', this.handleDragEnd)
      this.removeEventListener('cse-upload-validation-error', this.handleFileValidationError as EventListener)
    }
  }

  // Product search functionality
  private searchProductByCode = async (productCode: string): Promise<ProductSearchResult | null> => {
    if (!this.searchProductUrl) {
      console.warn('No search URL provided')
      return null
    }
    
    const request = new SpeedOrderSearchRequest(this.searchProductUrl, productCode)
    return this.searchService.searchByCode(request)
  }

  private handleProductCodeInput = async (rowIndex: number, productCode: string) => {
    // Reset error and loading states for this row
    const updatedRows = [...this.rows]
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      productCode: productCode,
      hasError: false,
      isSearching: false
    }
    this.rows = updatedRows

    // Directly perform search since we're now using @change event instead of @input with debouncing
    await this.performProductSearch(rowIndex, productCode)
  }

  private performProductSearch = async (rowIndex: number, productCode: string) => {
    if (!productCode.trim()) {
      return
    }

    // Set loading state
    const updatedRows = [...this.rows]
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      isSearching: true,
      hasError: false
    }
    this.rows = updatedRows

    try {
      const result = await this.searchProductByCode(productCode)
      
      // Get fresh rows state in case it changed during the async operation
      const currentRows = [...this.rows]
      
      if (result && result.items && result.items.length > 0) {
        const product = result.items[0]
        
        if (!product) {
          // Product is undefined - show error state
          currentRows[rowIndex] = {
            ...currentRows[rowIndex],
            isSearching: false,
            hasError: true,
            filled: false
          }
          this.rows = currentRows
          return
        }

        const price = product.sale_price_no_format ?? 
                     (product.prices?.sale ? parseFloat(product.prices.sale) : 0)
        const defaultQuantity = product.order_default_qty ?? 
                               product.ordering?.order_quantity ?? 
                               product.ordering?.minimum_order_quantity ?? 1

        // Fill the row with product data (spread to keep rowId)
        currentRows[rowIndex] = {
          ...currentRows[rowIndex],
          filled: true,
          productId: product.id,
          productName: product.name,
          productCode: product.code,
          price: price,
          quantity: defaultQuantity,
          isSearching: false,
          hasError: false,
          currencySymbol: product.currency_symbol || '€',
          defaultUom: product.default_uom || product.ordering?.default_uom || 'Per piece',
          pricePerPriceUom: product.price_per_price_uom ?? undefined,
          priceUom: product.price_uom ?? undefined,
          orderMinimumQty: product.order_minimum_qty || product.ordering?.minimum_order_quantity || 1,
          orderDefaultQty: product.order_default_qty || product.ordering?.order_quantity || 1
        }
      } else {
        // No product found - show error state
        currentRows[rowIndex] = {
          ...currentRows[rowIndex],
          isSearching: false,
          hasError: true,
          filled: false
        }
      }
      
      this.rows = currentRows
    } catch {
      // Search failed - show error state
      const currentRows = [...this.rows]
      currentRows[rowIndex] = {
        ...currentRows[rowIndex],
        isSearching: false,
        hasError: true,
        filled: false
      }
      this.rows = currentRows
    }
  }

  private handleQuantityChange = (rowIndex: number, quantity: string) => {
    const updatedRows = [...this.rows]
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      quantity: quantity
    }
    this.rows = updatedRows
  }

  private createEmptyRow(): SpeedOrderRow {
    return { rowId: `row-${this.nextRowId++}` }
  }

  private handleRowDelete = (rowIndex: number) => {
    this.rows = this.rows.filter((_, index) => index !== rowIndex)
  }

  private addRow() {
    this.rows = [...this.rows, this.createEmptyRow()]
  }

  private handleRequestNewRow = async () => {
    this.addRow()
    await this.updateComplete

    Array.from(this.formRows).at(-1)?.focusProductCode()
  }

  private emptyForm() {
    this.rows = Array.from({ length: 10 }, () => this.createEmptyRow())
  }

  private processImportedRecords(records: any[]) {
    // Clear existing rows first
    this.rows = []
    
    // Transform records from API format to component format (only filled rows)
    this.rows = records.map((record: any) => {
      const product = record.items && record.items[0] ? record.items[0] : {}
      return {
        rowId: `row-${this.nextRowId++}`,
        filled: true,
        hasError: false,
        isSearching: false,
        productCode: record.code || product.code || '',
        productName: product.name || '',
        quantity: record.qty || 1,
        price: product.sale_price_no_format || product.price || 0,
        productId: product.id || null,
        currencySymbol: product.currency_symbol || '€',
        defaultUom: product.default_uom || product.ordering?.default_uom || 'Per piece',
        pricePerPriceUom: product.price_per_price_uom ?? undefined,
        priceUom: product.price_uom ?? undefined,
        orderMinimumQty: product.order_minimum_qty || product.ordering?.minimum_order_quantity || 1,
        orderDefaultQty: product.order_default_qty || product.ordering?.order_quantity || 1
      }
    })
    
    // Force a re-render by updating the state
    this.requestUpdate()
  }

  private handleImportRemainingProducts = () => {
    if (this.importResult?.records) {
      this.processImportedRecords(this.importResult.records)
    }
    this.uploadSlideIn.close()
  }

  private hasValidProducts(): boolean {
    return this.rows.some(row => row.filled && row.productId && row.quantity)
  }

  private handleOrderAll = async () => {
    if (!this.rows.length) {
      return
    }

    this.isOrderAllLoading = true

    try {
      // Format products for the API
      const products = this.rows
        .filter(row => row.filled && row.productId && row.quantity)
        .map(row => [row.productId, row.quantity] as [number, number | string])

      if (!products.length) {
        this.isOrderAllLoading = false
        return
      }

      const request = new SpeedOrderAddToBasketRequest(this.addToBasketUrl, products)
      const response = await this.addToBasketService.addToBasket(request)

      if (response.result) {
        this.emptyForm()

        const successMessage = String(
          t('products_added_to_basket', { ns: 'csc-speed-order', default: 'Products added' })
        )

        const actionText = String(
          t('go_to_basket', { ns: 'csc-speed-order', default: 'View basket' })
        )

        showToast({
          variant: 'success',
          message: successMessage,
          action: {
            text: actionText,
            variant: 'info',
            url: this.basketUrl,
          },
        })

        document.dispatchEvent(new ProductAddedEvent(products.length))
      } else {
        const errorMessage = response.message || response.error || String(
          t('order_all_failed', { ns: 'csc-speed-order', default: 'Could not add products to basket' })
        )

        showToast({
          variant: 'error',
          message: errorMessage,
        })
      }
    } catch (error) {
      console.error('Error adding products to basket:', error)

      showToast({
        variant: 'error',
        message: String(
          t('order_all_failed', { ns: 'csc-speed-order', default: 'Could not add products to basket' })
        ),
      })
    } finally {
      this.isOrderAllLoading = false
    }
  }

  override render() {
    return html`
    <!-- Upload Results Slide-in -->
    ${this.disableUpload ? nothing : html`
      <csc-speed-order-slide-in
        variant="${this.slideInVariant}"
        ?has-headers=${this.hasHeaders}
        .column-headers=${this.uploadResult?.header_cols || []}
        @upload-csv="${this.validateAndImportCSV}"
        @import-remaining-products="${this.handleImportRemainingProducts}"
      ></csc-speed-order-slide-in>
    `}

    <div class="main-container">
      <div class="speed-order-section">
        <cse-group direction="column" row-gap="08" class="speed-order-container font-body"
                 @dragover=${this.disableUpload ? nothing : this.handleDragOver}
                 @drop=${this.disableUpload ? nothing : this.handleDrop}
                 @dragenter=${this.disableUpload ? nothing : this.handleDragEnter}
                 @dragleave=${this.disableUpload ? nothing : this.handleDragLeave}
                 @dragend=${this.disableUpload ? nothing : this.handleDragEnd}>
        ${this.isOrderAllLoading ? html`
          <div class="loader-wrapper">
            <cse-loader animated></cse-loader>
          </div>
        ` : nothing}

        <!-- Upload Loading State -->
        ${!this.disableUpload && this.isUploading ? html`
          <div class="loader-wrapper">
            <cse-loader animated></cse-loader>
            <cse-text>Uploading file...</cse-text>
          </div>
        ` : nothing}

        <!-- Drag and Drop Overlay -->
        ${!this.disableUpload && this.isDragOver ? html`
          <div class="drag-overlay">
            <div class="drag-overlay-content">
              <cse-icon icon-name="files" icon-style="light" class="drag-icon" aria-hidden="true"></cse-icon>
              <cse-text variant="heading-3" class="drag-text">
                ${t('upload_files_here', { ns: 'csc-speed-order', default: 'Upload files here' })}
              </cse-text>
            </div>
          </div>
        ` : nothing}

        <cse-group direction="column" row-gap="05">
          <cse-text variant="heading-1" as="h1" class="speed-order-title" >
            ${t('title', { ns: 'csc-speed-order', default: 'Speed Order' })}
          </cse-text>
          <cse-text class="speed-order-description">
            ${t('description', { ns: 'csc-speed-order', default: 'Enter the product code and the quantity per product.' })}
          </cse-text>
        </cse-group>

        ${this.disableUpload ? nothing : html`
          <div class="alert-container">
            <cse-paper variant="secondary">
              <cse-text slot="heading">
                ${t('info', { ns: 'csc-speed-order', default: 'You can drag and drop a CSV file on the page or choose from your files. The file must contain two columns: product code and quantity.' })}
              </cse-text>
              <div class="button-link-container">
                <div class="button-link-container-inner">
              <cse-button variant="secondary" @click=${this.triggerFileUpload}>
                <cse-icon icon-name="circle-plus" icon-style="light" slot="prefix" aria-hidden="true"></cse-icon>
                ${t('choose_file', { ns: 'csc-speed-order', default: 'Choose file' })}
              </cse-button>
              <cse-link url="${this.dataExampleUrl}" download="speedorder-example.csv">
                ${t('download_example', { ns: 'csc-speed-order', default: 'Download example' })}
              </cse-link>
              </div>
              ${this.fileValidationError ? html`
                <cse-text variant="body-s" class="file-validation-error">
                  ${this.fileValidationError}
                </cse-text>
              ` : nothing}
              </div>
            </cse-paper>

            <input
              type="file"
              id="csv-file-input"
              accept=".csv,text/csv"
              style="display: none;"
              @change=${this.handleFileInputChange}>
          </div>
        `}

        <cse-list>
        ${repeat(
          this.rows,
          (row, index) => row.rowId ?? `row-${index}`,
          (row, index) => html`
          <csc-speed-order-form-row
            ?filled=${row.filled}
            ?is-last=${index === this.rows.length - 1}
            product-name=${row.productName || ''}
            product-code=${row.productCode || ''}
            .price=${row.price ?? 0}
            .quantity=${String(row.quantity ?? '')}
            ?is-searching=${row.isSearching}
            ?has-error=${row.hasError}
            currency-symbol=${row.currencySymbol || '€'}
            default-uom=${row.defaultUom || 'Per piece'}
            price-per-price-uom=${this.uomSource === 'price' ? row.pricePerPriceUom ?? '' : ''}
            price-uom=${this.uomSource === 'price' ? row.priceUom ?? '' : ''}
            .order-minimum-qty=${row.orderMinimumQty ?? 1}
            .order-default-qty=${row.orderDefaultQty ?? 1}
            @product-code-input=${(e: CustomEvent) => this.handleProductCodeInput(index, e.detail.value)}
            @quantity-change=${(e: CustomEvent) => this.handleQuantityChange(index, e.detail.value)}
            @delete-row=${() => this.handleRowDelete(index)}
            @request-new-row=${this.handleRequestNewRow}
          ></csc-speed-order-form-row>
        `)}
        </cse-list>
        <div class="buttons-container">
            <div class="form-button-group">
              <cse-button variant="default" fit="fill" id="add-line-button" @click=${this.addRow}>
                <cse-icon icon-name="plus" icon-style="light" slot="prefix" aria-hidden="true"></cse-icon>
                ${t('add_line', { ns: 'csc-speed-order', default: 'Add line' })}
              </cse-button>
              <cse-button variant="default" fit="fill" id="empty-form-button" @click=${this.emptyForm}>
                <cse-icon icon-name="sync" icon-style="light" slot="prefix" aria-hidden="true"></cse-icon>
                ${t('empty_form', { ns: 'csc-speed-order', default: 'Reset form' })}
              </cse-button>
            </div>
            
            <cse-button
              variant="primary"
              fit="${this.isMobileScreen ? 'fill' : 'auto'}"
              class="order-all-button"
              ?disabled=${this.isOrderAllLoading || !this.hasValidProducts()}
              @click=${this.handleOrderAll}
            >
              ${t('order_all', { ns: 'csc-speed-order', default: 'Order all' })}
            </cse-button>
        </div>
      </cse-group>
      </div>
      
    </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-speed-order': CSCSpeedOrder
  }
}
